// Eskom PARCC — pure logic for the Manage Transactions admin screen.
//
// React-free, Firebase-free helpers so they unit-test in isolation. The Manage
// screen lets an admin add a brand-new transaction or edit an existing one's base
// fields; this module turns the screen's form state into the exact payloads the
// `useUpsertTransaction` hook (and through it `upsertTransaction` in dbWrite)
// accepts, and provides the uniqueness pre-check for Add.
//
// Two payload builders, mirroring the two write modes:
//   • `buildManageInsert` — the FULL insert payload (Add). Carries the insert-only
//     identity fields (referenceNo/baseNo/recordLabel/year/csdNumber) plus
//     every writable base column. `upsertTransaction` inserts because the ref is
//     new.
//   • `buildManageUpdate` — the writable-UPDATE subset only (Edit). Deliberately
//     OMITS the insert-only identity fields: the UpdateTransactionData mutation
//     cannot change them (see dbWrite `TransactionUpdateInput`), so the editor
//     renders them read-only and this builder never emits them. `period` is NOT
//     among them since Aug 2026 (owner feedback: unable to update period on
//     Manage or the bulk update) — the connector now updates it, so it is
//     emitted here and editable in both modes. `referenceNo` is
//     still carried because it is the upsert *key* (how the hook resolves the
//     row), not an updated column — `transactionUpdateVars` strips it.
//
// `value` is parsed from the free-text Rand field via the shared `parseRand`
// (so "R 1 234 567" and "12,500" both work) and a matching `valueDisplay` is
// derived via `formatRand`. Emails are normalised. Blank optional fields collapse
// to `undefined` so the write layer's mappers omit them (a tidy insert / a
// no-op-free update).
import { parseRand, formatRand } from '../../lib/money';
import type { Transaction } from '../../data/types';
import type { TransactionInsertInput } from '../../lib/dbWrite';
import { normalizeGapStatus } from '../../domain/normalize';

/**
 * The Manage editor's form state. A flat, all-string shape (what the inputs hold)
 * — `value` is the free-text Rand field, the rest are plain text / select values.
 * The builders coerce/normalise on the way out.
 */
export interface ManageForm {
  // Insert-only identity fields (read-only in Edit mode).
  referenceNo: string;
  baseNo: string;
  recordLabel: string;
  year: string;
  /** Editable in BOTH modes since Aug 2026 — the one former identity field the
   * connector now updates (owner feedback: period corrections on existing
   * transactions). */
  period: string;
  csdNumber: string;
  // Writable base fields (editable in both modes).
  dataset: string;
  division: string;
  supplier: string;
  /** Free-text Rand value, parsed via {@link parseRand}. */
  value: string;
  mechanism: string;
  link: string;
  /** "Link provided" flag as a select value: '' (unset) | 'Yes' | 'No'. */
  linkProvided: string;
  /** "Issue with links" free-text answer (owner feedback #8: editable link status). */
  linkIssue: string;
  gapsStatus: string;
  complianceLevel: string;
  complianceLevelOverride: string;
  selfDeclaration: string;
  reviewStatus: string;
}

/** A blank form for Add mode (every field empty). */
export const EMPTY_MANAGE_FORM: ManageForm = {
  referenceNo: '',
  baseNo: '',
  recordLabel: '',
  year: '',
  period: '',
  csdNumber: '',
  dataset: '',
  division: '',
  supplier: '',
  value: '',
  mechanism: '',
  link: '',
  linkProvided: '',
  linkIssue: '',
  gapsStatus: '',
  complianceLevel: '',
  complianceLevelOverride: '',
  selfDeclaration: '',
  reviewStatus: '',
};

/** Trimmed value, or `undefined` when blank (so the write mapper omits it). */
function opt(v: string): string | undefined {
  const t = v.trim();
  return t === '' ? undefined : t;
}

/** Coerce a Yes/No select value to a boolean, or `undefined` when unset. */
function optBool(v: string): boolean | undefined {
  const t = v.trim().toLowerCase();
  if (t === 'yes' || t === 'true') return true;
  if (t === 'no' || t === 'false') return false;
  return undefined;
}

/**
 * Seed a {@link ManageForm} from an existing transaction (Edit mode). Numeric
 * `value` is rendered back through {@link formatRand} into the editable Rand field
 * so the displayed string round-trips through {@link parseRand} on save.
 */
export function transactionToForm(tx: Transaction): ManageForm {
  return {
    referenceNo: tx.referenceNo ?? '',
    baseNo: tx.baseNo ?? '',
    recordLabel: tx.recordLabel ?? '',
    year: tx.year ?? '',
    period: tx.period ?? '',
    csdNumber: tx.csdNumber ?? '',
    dataset: tx.dataset ?? '',
    division: tx.division ?? '',
    supplier: tx.supplier ?? '',
    value: tx.value != null ? formatRand(tx.value) : '',
    mechanism: tx.mechanism ?? '',
    link: tx.link ?? '',
    linkProvided: tx.linkProvided === true ? 'Yes' : tx.linkProvided === false ? 'No' : '',
    linkIssue: tx.linkIssue ?? '',
    // Normalised so a legacy value matches the four-state select options.
    gapsStatus: normalizeGapStatus(tx.gapsStatus),
    complianceLevel: tx.complianceLevel ?? '',
    complianceLevelOverride: tx.complianceLevelOverride ?? '',
    selfDeclaration: tx.selfDeclaration ?? '',
    reviewStatus: tx.reviewStatus ?? '',
  };
}

/** Normalise a reference for uniqueness comparison: trimmed, lower-cased, inner whitespace collapsed. */
export function normalizeRef(ref: string | null | undefined): string {
  if (ref == null) return '';
  return ref.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Would adding `ref` collide with an existing transaction's reference?
 *
 * Compares case- and whitespace-insensitively via {@link normalizeRef}. In Edit
 * mode pass `currentRef` (the reference being edited) so the row's own reference
 * never counts as a duplicate of itself. Soft-deleted transactions still count —
 * the DB unique constraint covers them, so reusing a deleted ref would still
 * collide. A blank `ref` is never a duplicate (it is caught as "required" first).
 */
export function isDuplicateRef(
  ref: string,
  transactions: readonly Pick<Transaction, 'referenceNo'>[],
  currentRef?: string | null,
): boolean {
  const target = normalizeRef(ref);
  if (target === '') return false;
  const current = normalizeRef(currentRef);
  return transactions.some((t) => {
    const existing = normalizeRef(t.referenceNo);
    if (existing === '') return false;
    if (current !== '' && existing === current) return false; // the row itself
    return existing === target;
  });
}

/**
 * Find a REMOVED (soft-deleted) transaction whose reference matches `ref`, so the
 * Add form can tell an admin to restore it rather than silently false-succeed on
 * a hidden row (Lindiwe R4 feedback #6). A live match takes precedence (returns
 * undefined) — that is an ordinary duplicate, handled by {@link isDuplicateRef}.
 */
export function findRemovedByRef(
  ref: string,
  transactions: readonly Pick<Transaction, 'referenceNo' | 'deleted'>[],
): (Pick<Transaction, 'referenceNo' | 'deleted'>) | undefined {
  const target = normalizeRef(ref);
  if (target === '') return undefined;
  const matches = transactions.filter((t) => normalizeRef(t.referenceNo) === target);
  if (matches.length === 0) return undefined;
  // A live row wins — that is a normal duplicate, not a restore case.
  if (matches.some((t) => !t.deleted)) return undefined;
  return matches[0];
}

/**
 * Build the FULL insert payload (Add mode). Carries the insert-only identity
 * fields plus every writable base column. `value` is parsed via {@link parseRand};
 * a matching `valueDisplay` is derived only when a number was parsed. Blank
 * optional fields are omitted (the write mapper treats absent as unset).
 * `useUpsertTransaction` inserts because the reference is new.
 */
export function buildManageInsert(form: ManageForm): TransactionInsertInput {
  const value = parseRand(form.value);
  const input: TransactionInsertInput = { referenceNo: form.referenceNo.trim() };
  setIf(input, 'baseNo', opt(form.baseNo));
  setIf(input, 'recordLabel', opt(form.recordLabel));
  setIf(input, 'dataset', opt(form.dataset));
  setIf(input, 'year', opt(form.year));
  setIf(input, 'period', opt(form.period));
  setIf(input, 'division', opt(form.division));
  setIf(input, 'supplier', opt(form.supplier));
  setIf(input, 'csdNumber', opt(form.csdNumber));
  if (value != null) {
    input.value = value;
    input.valueDisplay = formatRand(value);
  }
  setIf(input, 'mechanism', opt(form.mechanism));
  setIf(input, 'gapsStatus', opt(form.gapsStatus));
  setIf(input, 'complianceLevel', opt(form.complianceLevel));
  setIf(input, 'complianceLevelOverride', opt(form.complianceLevelOverride));
  setIf(input, 'selfDeclaration', opt(form.selfDeclaration));
  setIf(input, 'link', opt(form.link));
  setIf(input, 'linkProvided', optBool(form.linkProvided));
  setIf(input, 'linkIssue', opt(form.linkIssue));
  setIf(input, 'reviewStatus', opt(form.reviewStatus));
  return input;
}

/**
 * Build the writable-UPDATE payload (Edit mode). Carries the upsert KEY
 * (`referenceNo`) plus only the columns the UpdateTransactionData mutation
 * accepts; the insert-only identity fields (baseNo/recordLabel/year/
 * csdNumber) are deliberately NOT emitted — the connector cannot change them, so
 * they stay read-only in the editor. `period` IS emitted (updatable since the
 * Aug 2026 period-correction feedback). `transactionUpdateVars` further strips
 * `referenceNo` (it is the key, not an updated column). `value` is parsed via
 * {@link parseRand}; `valueDisplay` derived only when a number was parsed.
 */
export function buildManageUpdate(form: ManageForm): TransactionInsertInput {
  const value = parseRand(form.value);
  const input: TransactionInsertInput = { referenceNo: form.referenceNo.trim() };
  setIf(input, 'period', opt(form.period));
  setIf(input, 'dataset', opt(form.dataset));
  setIf(input, 'division', opt(form.division));
  setIf(input, 'supplier', opt(form.supplier));
  if (value != null) {
    input.value = value;
    input.valueDisplay = formatRand(value);
  }
  setIf(input, 'mechanism', opt(form.mechanism));
  setIf(input, 'gapsStatus', opt(form.gapsStatus));
  setIf(input, 'complianceLevel', opt(form.complianceLevel));
  setIf(input, 'complianceLevelOverride', opt(form.complianceLevelOverride));
  setIf(input, 'selfDeclaration', opt(form.selfDeclaration));
  setIf(input, 'link', opt(form.link));
  setIf(input, 'linkProvided', optBool(form.linkProvided));
  setIf(input, 'linkIssue', opt(form.linkIssue));
  setIf(input, 'reviewStatus', opt(form.reviewStatus));
  return input;
}

/** Assign only when defined (keeps payloads free of explicit undefineds). */
function setIf<K extends keyof TransactionInsertInput>(
  input: TransactionInsertInput,
  key: K,
  value: TransactionInsertInput[K] | undefined,
): void {
  if (value !== undefined) input[key] = value;
}
